import type { StudioSession } from '@shared';

/**
 * The studio's session, as the browser sees it.
 *
 * Outside Discord there is no SDK handshake to run: the server holds a sealed
 * cookie, and this module's whole job is to ask whether it is still good and to
 * send the model to Discord when it isn't. No token ever reaches this file —
 * that is the point of doing it with a cookie rather than the iframe's bearer.
 */

/** Why a session could not be opened, in the words the login screen uses. */
export type SignedOutReason =
  | 'none'
  /** Signed in with Discord, but not the owner and not a profile's manager. */
  | 'staff'
  /** She dismissed Discord's consent screen. */
  | 'declined'
  /** The handshake went stale or arrived out of order — start again. */
  | 'stale'
  /** Discord accepted her but the token exchange failed on our side. */
  | 'exchange';

export type StudioSessionResult =
  | { kind: 'session'; session: StudioSession }
  | { kind: 'signed-out' }
  /** Held a session, no longer qualifies — the server has already dropped it. */
  | { kind: 'denied' };

const LOGIN_PATH = '/api/studio/login';
const LOGOUT_PATH = '/api/studio/logout';
const SESSION_PATH = '/api/studio/session';

export async function fetchStudioSession(): Promise<StudioSessionResult> {
  const response = await fetch(SESSION_PATH, {
    // Same-origin in every deployment we ship, but stated rather than assumed:
    // the cookie is the credential, and a split-host setup would silently drop
    // it under fetch's default.
    credentials: 'include',
    headers: { Accept: 'application/json' },
  });

  if (response.status === 401) return { kind: 'signed-out' };
  if (response.status === 403) return { kind: 'denied' };
  if (!response.ok) {
    throw new Error(`The studio backend answered ${response.status}`);
  }
  return { kind: 'session', session: (await response.json()) as StudioSession };
}

/** Hands the page over to Discord. Never returns. */
export function signIn(): void {
  window.location.href = LOGIN_PATH;
}

/** Drops the cookie, then reloads into the signed-out state. */
export async function signOut(): Promise<void> {
  await fetch(LOGOUT_PATH, { method: 'POST', credentials: 'include' }).catch(() => {
    // A failed logout still has to land somewhere sensible; the reload below
    // will simply find the session intact and say so.
  });
  window.location.replace('/');
}

/**
 * Reads the reason the callback bounced her back here, then scrubs it from the
 * address bar — a refusal that survives a refresh reads as a broken app.
 */
export function readSignedOutReason(): SignedOutReason {
  const params = new URLSearchParams(window.location.search);
  const raw = params.get('error');
  if (!raw) return 'none';

  params.delete('error');
  const query = params.toString();
  window.history.replaceState(null, '', `${window.location.pathname}${query ? `?${query}` : ''}`);

  switch (raw) {
    case 'staff':
      return 'staff';
    case 'declined':
      return 'declined';
    case 'exchange':
      return 'exchange';
    case 'state':
    case 'nocode':
      return 'stale';
    default:
      return 'none';
  }
}
